import Axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import Select from "react-select";
import { checkAccessList } from "../common/utilities";
import CustomAccordion from "../components/CustomAccordion";
import { USER_TRAINING_GOALS } from "../constants/urlConstants";
import NewChart from "./ProgressBar/NewChart";
import * as XLSX from "xlsx";

// Flag image mapping
const FLAG_IMAGES = {
  India: require("../assets/india.png"),
  Canada: require("../assets/canada.png"),
  US: require("../assets/united-states.png"),
};

// Helper: Normalize truthy "true"/true
const isTrue = (v) =>
  v === true || (typeof v === "string" && v.toLowerCase() === "true");

// Helper: Calculate training progress
// Uses boolean is_pass from the user's modules; falls back to numeric 1 if present.
const calculateProgress = (training, masterTrainings) => {
  const masterTraining = masterTrainings.find(
    (t) => t.training_id === training.training_id
  );
  if (!masterTraining) return 0;

  const totalModules = (masterTraining.modules || []).length;
  if (totalModules === 0) return 0;

  const passedModules = (training.modules || []).filter(
    (m) => m?.is_pass === true || m?.is_pass === 1
  ).length;

  return Math.round((passedModules / totalModules) * 100);
};

// Header for accordion item
const TrainingHeader = ({ employee, training }) => (
  <Row style={{ width: "100rem" }}>
    <Col className="flex-two">
      {FLAG_IMAGES[employee.geography] && (
        <img
          src={FLAG_IMAGES[employee.geography]}
          alt={employee.geography}
          style={{ width: 24, height: 16, marginRight: 6, verticalAlign: "middle" }}
        />
      )}
      {employee.employee_name}
    </Col>
    <Col className="flex-two">
      (Band {employee.band}) {employee.competency}
    </Col>
    <Col className="flex-two">
      {training.training_name ? <b>{training.training_name}</b> : "No Training"}
    </Col>
    <Col style={{ width: "20%" }}>{training.progress_percentage || 0}%</Col>
  </Row>
);

// Body for accordion item
const TrainingBody = ({ employee, training, masterTrainings }) => {
  const masterTraining = masterTrainings.find(
    (t) => t.training_id === training.training_id
  );
  const modules = masterTraining?.modules || [];
  const userModules = training.modules || [];

  const renderCertLinks = (links = []) => {
    if (!Array.isArray(links) || links.length === 0) return "-";
    // Render as comma-separated "Link 1, Link 2, ..."
    return links.map((url, idx) => (
      <a
        key={url + idx}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginRight: 8 }}
      >
        Link {idx + 1}
      </a>
    ));
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <table className="table table-striped table-bordered table-hover" style={{ flex: 1 }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Requires</th>
            <th>Status</th>
            <th>Score %</th>
            <th>Certificates</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((mod) => {
            const userMod = userModules.find((um) => um.module_id === mod.module_id);

            const requiresCert = isTrue(mod.requires_certification);
            const modStatus = userMod?.mod_status ?? "-";
            const passed = userMod?.is_pass === true || userMod?.is_pass === 1;
            const scorePct =
              typeof userMod?.score_percentage === "number"
                ? userMod.score_percentage
                : userMod?.score_percentage ?? "-";
            const certLinks = userMod?.certificate_links ?? [];

            return (
              <tr key={mod.module_id || mod.module_name}>
                <td>{mod.module_name}</td>
                <td>
                  {requiresCert ? (
                    <div>
                      <img
                        src={require("../assets/certificate.png")}
                        alt="Certification"
                        style={{ width: 20, height: 20 }}
                      />{" "}
                      Certification
                    </div>
                  ) : (
                    <span>{mod.cut_off}%</span>
                  )}
                </td>
                <td>
                  {userMod
                    ? passed
                      ? "Pass"
                      : modStatus !== "-" // show explicit status if provided
                        ? modStatus
                        : "Fail"
                    : "-"}
                </td>
                <td>{userMod ? `${scorePct}${scorePct === "-" ? "" : "%"}` : "-"}</td>
                <td>{renderCertLinks(certLinks)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ minWidth: "200px" }}>
        <NewChart series={training.progress_percentage} />
      </div>
    </div>
  );
};

const TrainingGoals = (props) => {
  const { currentUserDetail, staticValues, trainings } = props;

  const [trainingStatus, setTrainingStatus] = useState({ label: "Completed", value: 100 });
  const [region, setRegion] = useState({ label: "All", value: "All" });
  const [competency, setCompetency] = useState({ label: "All", value: 0 });
  const [band, setBand] = useState({ label: "All", value: 0 });
  const [team, setTeam] = useState({ label: "All", value: 0 });
  const [training, setTraining] = useState({ label: "All", value: 0 });
  const [searchTerm, setSearchTerm] = useState("");

  const [trainingOptions, setTrainingOptions] = useState([]);
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [bandOptions, setBandOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  const [masterTrainings, setMasterTrainings] = useState([]);
  const [masterGoals, setMasterGoals] = useState([]);
  const [isLoading, setLoading] = useState(false);

  // Load static values for dropdowns
  useEffect(() => {
    if (!staticValues) return;
    const competencies = [
      { value: 0, label: "All" },
      ...staticValues?.filter((x) => x.type === "competency").map((x) => ({ value: x.id, label: x.description }))
    ];
    const bands = [
      { value: 0, label: "All" },
      ...staticValues?.filter((x) => x.type === "band").map((x) => ({ value: x.id, label: x.description }))
    ];
    const teams = [
      { value: 0, label: "All" },
      ...staticValues?.filter((x) => x.type === "team").map((x) => ({ value: x.id, label: x.description }))
    ];
    setCompetencyOptions(competencies);
    setBandOptions(bands);
    setTeamOptions(teams);
  }, [staticValues]);

  // Load trainings (master catalog)
  useEffect(() => {
    if (!trainings) return;
    const filteredTrainings = trainings.filter((t) => t.is_mandatory !== "true");
    const sortedTrainings = [...filteredTrainings].sort((a, b) =>
      a.training_name.localeCompare(b.training_name)
    );
    setMasterTrainings(sortedTrainings);
    setTrainingOptions([
      { value: 0, label: "All" },
      ...sortedTrainings.map((x) => ({ value: x.training_id, label: x.training_name })),
    ]);
  }, [trainings]);

  // Load employee training goals
  useEffect(() => {
    setLoading(true);
    Axios.get(USER_TRAINING_GOALS)
      .then((res) => {
        const goals = res.data || [];
        setMasterGoals(goals);
        setLoading(false);
      })
      .catch(() => {
        setMasterGoals([]);
        setLoading(false);
      });
  }, []);

  // Apply filters
  const filteredGoals = useMemo(() => {
    let result = Array.isArray(masterGoals) ? [...masterGoals] : [];

    // Region filter
    if (region.value !== "All") {
      result = result.filter((e) => e.geography === region.label);
    }

    // Competency filter
    if (competency.value !== 0) {
      result = result.filter((e) => e.competency === competency.label);
    }

    // Band filter
    if (band.value !== 0) {
      result = result.filter((e) => e.band === band.label);
    }

    // Team filter
    if (team.value !== 0) {
      result = result.filter((e) => e.team === team.label);
    }

    // Search filter
    if (searchTerm.trim()) {
      result = result.filter((e) =>
        e.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Training filter
    if (training.value !== 0) {
      result = result.map((e) => ({
        ...e,
        trainings: (e.trainings || []).filter(
          (t) => t.training_id === training.value
        ),
      }));
    }

    // Attach (re)computed progress using new boolean is_pass
    result = result.map((e) => ({
      ...e,
      trainings: (e.trainings || []).map((t) => ({
        ...t,
        progress_percentage: calculateProgress(t, masterTrainings),
      })),
    }));

    // Status filter
    if (trainingStatus.value !== -1) {
      result = result
        .map((e) => {
          if (!e.trainings || e.trainings.length === 0) {
            return {
              ...e,
              trainings:
                trainingStatus.value === 0
                  ? [{}] // placeholder to keep employee in result
                  : [],
            };
          }
          return {
            ...e,
            trainings: e.trainings.filter((t) => {
              if (trainingStatus.value === 100) {
                return t.progress_percentage === 100;
              }
              if (trainingStatus.value === 50) {
                return t.progress_percentage > 0 && t.progress_percentage < 100;
              }
              if (trainingStatus.value === 0) {
                return t.progress_percentage === 0;
              }
              return true;
            }),
          };
        })
        .filter((e) => e.trainings.length > 0);
    }

    // Sort by highest progress
    result.sort((a, b) => {
      const aMax = Math.max(...(a.trainings || []).map((t) => t.progress_percentage || 0), 0);
      const bMax = Math.max(...(b.trainings || []).map((t) => t.progress_percentage || 0), 0);
      return bMax - aMax;
    });

    return result;
  }, [masterGoals, region, competency, band, team, training, trainingStatus, searchTerm, masterTrainings]);

  const getSelectedTrainingDetails = () => {
    return masterTrainings.find((t) => t.training_id === training.value) || null;
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = [];
    filteredGoals.forEach((e) => {
      if ((e.trainings || []).length > 1) {
        e.trainings.forEach((t) => {
          const masterTraining = masterTrainings.find(mt => mt.training_id === t.training_id);
          const masterModules = masterTraining ? (masterTraining.modules || []) : [];

          const userModulesMap = {};
          if (Array.isArray(t.modules)) {
            t.modules.forEach(mod => {
              userModulesMap[mod.module_id] = mod;
            });
          }

          if (masterModules.length) {
            masterModules.forEach((masterMod) => {
              const userMod = userModulesMap[masterMod.module_id];
              const passed = userMod?.is_pass === true || userMod?.is_pass === 1;
              const links = Array.isArray(userMod?.certificate_links)
                ? userMod.certificate_links.join(", ")
                : "";
              exportData.push({
                "Employee ID": e.employee_id,
                "Employee Name": e.employee_name,
                "Employee Email": e.email_id,
                Region: e.geography,
                Band: e.band,
                Competency: e.competency,
                Training: t.training_name,
                "Progress (%)": t.progress_percentage || 0,
                "Module Name": masterMod.module_name,
                "Requires Cert": isTrue(masterMod.requires_certification) ? "Yes" : "No",
                "Status": userMod?.mod_status ?? (passed ? "Pass" : userMod ? "Fail" : "-"),
                "Score %": userMod?.score_percentage ?? "-",
                "Certificates": links || "-",
              });
            });
          } else {
            exportData.push({
              "Employee ID": e.employee_id,
              "Employee Name": e.employee_name,
              "Employee Email": e.email_id,
              Region: e.geography,
              Band: e.band,
              Competency: e.competency,
              Training: t.training_name,
              "Progress (%)": t.progress_percentage || 0,
              "Module Name": "-",
              "Requires Cert": "-",
              "Status": "-",
              "Score %": "-",
              "Certificates": "-",
            });
          }
        });
      } else {
        let selectedTraining = getSelectedTrainingDetails();
        if (selectedTraining) {
          const masterModules = selectedTraining.modules || [];
          const userTraining = (e.trainings && e.trainings.length === 1) ? e.trainings[0] : {};
          const userModulesMap = {};
          if (Array.isArray(userTraining.modules)) {
            userTraining.modules.forEach(mod => {
              userModulesMap[mod.module_id] = mod;
            });
          }
          if (masterModules.length) {
            masterModules.forEach((masterMod) => {
              const userMod = userModulesMap[masterMod.module_id];
              const passed = userMod?.is_pass === true || userMod?.is_pass === 1;
              const links = Array.isArray(userMod?.certificate_links)
                ? userMod.certificate_links.join(", ")
                : "";
              exportData.push({
                "Employee ID": e.employee_id,
                "Employee Name": e.employee_name,
                "Employee Email": e.email_id,
                Region: e.geography,
                Band: e.band,
                Competency: e.competency,
                Training: selectedTraining.training_name,
                "Progress (%)": userTraining.progress_percentage || 0,
                "Module Name": masterMod.module_name,
                "Requires Cert": isTrue(masterMod.requires_certification) ? "Yes" : "No",
                "Status": userMod?.mod_status ?? (passed ? "Pass" : userMod ? "Fail" : "-"),
                "Score %": userMod?.score_percentage ?? "-",
                "Certificates": links || "-",
              });
            });
          } else {
            exportData.push({
              "Employee ID": e.employee_id,
              "Employee Name": e.employee_name,
              "Employee Email": e.email_id,
              Region: e.geography,
              Band: e.band,
              Competency: e.competency,
              Training: selectedTraining.training_name,
              "Progress (%)": userTraining.progress_percentage || 0,
              "Module Name": "-",
              "Requires Cert": "-",
              "Status": "-",
              "Score %": "-",
              "Certificates": "-",
            });
          }
        } else {
          exportData.push({
            "Employee ID": e.employee_id,
            "Employee Name": e.employee_name,
            "Employee Email": e.email_id,
            Region: e.geography,
            Band: e.band,
            Competency: e.competency,
            Training: "-",
            "Progress (%)": "-",
            "Module Name": "-",
            "Requires Cert": "-",
            "Status": "-",
            "Score %": "-",
            "Certificates": "-",
          });
        }
      }
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Autofit columns based on cell text size
    const objectMaxLength = [];
    exportData.forEach(row => {
      Object.entries(row).forEach(([key, value], colIdx) => {
        const cellValue = value == null ? "" : value.toString();
        objectMaxLength[colIdx] = Math.max(
          objectMaxLength[colIdx] || key.length,
          cellValue.length
        );
      });
    });
    // Also check header lengths
    Object.keys(exportData[0] || {}).forEach((key, colIdx) => {
      objectMaxLength[colIdx] = Math.max(objectMaxLength[colIdx] || 0, key.length);
    });
    ws['!cols'] = objectMaxLength.map(w => ({ wch: w + 2 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TrainingGoals");
    XLSX.writeFile(wb, "training_goals.xlsx");
  };

  return (
    <div style={{ padding: "1%" }}>
      {/* Filters */}
      <Row className="mb-3">
        <Col>
          <Form.Label>Training</Form.Label>
          <Select value={training} options={trainingOptions} onChange={setTraining} />
        </Col>
        <Col>
          <Form.Label>Status</Form.Label>
          <Select
            value={trainingStatus}
            options={[
              { value: 100, label: "Completed" },
              { value: 50, label: "In Progress" },
              { value: 0, label: "Not Started" },
              { value: -1, label: "All" },
            ]}
            onChange={setTrainingStatus}
          />
        </Col>
        <Col>
          <Form.Label>Region</Form.Label>
          <Select
            value={region}
            options={[
              { value: "All", label: "All" },
              { value: "India", label: "India" },
              { value: "Canada", label: "Canada" },
              { value: "US", label: "US" },
            ]}
            onChange={setRegion}
          />
        </Col>
        <Col>
          <Form.Label>Team</Form.Label>
          <Select value={team} options={teamOptions} onChange={setTeam} />
        </Col>
        {checkAccessList("competency_selection") && (
          <>
            <Col>
              <Form.Label>Competency</Form.Label>
              <Select value={competency} options={competencyOptions} onChange={setCompetency} />
            </Col>
            <Col>
              <Form.Label>Band</Form.Label>
              <Select value={band} options={bandOptions} onChange={setBand} />
            </Col>
          </>
        )}
      </Row>
      <Row className="mb-3">
        <Col md={11}>
          <Form.Label>Search by Employee Name</Form.Label>
          <Form.Control
            type="text"
            value={searchTerm}
            placeholder="Enter name"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col className="d-flex align-items-end">
          <button className="btn btn-success" onClick={handleExportToExcel}>
            Export Excel
          </button>
        </Col>
      </Row>

      {/* Content */}
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "30rem" }}>
          <Spinner animation="grow" variant="primary" />
        </div>
      ) : filteredGoals.length === 0 ? (
        <div className="text-muted text-center mt-5">No matching training goals found.</div>
      ) : (
        <CustomAccordion
          items={filteredGoals.flatMap((e) =>
            (e.trainings || []).length
              ? e.trainings.map((t) => ({
                  header: <TrainingHeader employee={e} training={t} />,
                  body: <TrainingBody employee={e} training={t} masterTrainings={masterTrainings} />,
                }))
              : [
                  {
                    header: <TrainingHeader employee={e} training={{}} />,
                    body: <div className="text-muted p-3">This employee has no trainings assigned.</div>,
                  },
                ]
          )}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUserDetail: state.CurrentUserReducer.currentUserDetail,
});

export default connect(mapStateToProps)(TrainingGoals);
